/**
 * Hook to access auth context data passed from Django via window.__AUTH_CONTEXT__
 */

export interface SocialProvider {
  provider: string;
  name: string;
  loginUrl: string;
}

export interface FormErrors {
  [fieldName: string]: string[];
}

export type PageType =
  | "login"
  | "signup"
  | "password_reset"
  | "password_reset_confirm"
  | "password_reset_done"
  | "password_change_done"
  | "logout"
  | "email_confirm"
  | "no_team_access"
  | "social_login_confirm";

export interface AuthContext {
  csrfToken: string;
  formErrors: FormErrors;
  socialProviders: SocialProvider[];
  nextUrl: string;
  currentLanguage: string;
  availableLanguages: { code: string; name: string }[];
  pageType: PageType;
  // Page-specific data
  invitation?: {
    id: number;
    email: string;
  };
  tokenFail?: boolean;
  actionUrl?: string;
  isAuthenticated?: boolean;
  formData?: {
    email?: string;
    login?: string;
    teamName?: string;
  };
  // No team access page
  supportEmail?: string;
  // Social login confirm page
  provider?: {
    id: string;
    name: string;
  };
  process?: "login" | "connect";
}

declare global {
  interface Window {
    __AUTH_CONTEXT__?: AuthContext;
  }
}

const defaultContext: AuthContext = {
  csrfToken: "",
  formErrors: {},
  socialProviders: [],
  nextUrl: "/",
  currentLanguage: "en",
  availableLanguages: [
    { code: "en", name: "English" },
    { code: "de", name: "Deutsch" },
  ],
  pageType: "login",
};

export function useAuthContext(): AuthContext {
  if (typeof window === "undefined") {
    return defaultContext;
  }

  return window.__AUTH_CONTEXT__ ?? defaultContext;
}

/**
 * Get a specific form field's errors
 */
export function useFieldErrors(fieldName: string): string[] {
  const context = useAuthContext();
  return context.formErrors[fieldName] ?? [];
}

/**
 * Get non-field errors (general form errors)
 */
export function useNonFieldErrors(): string[] {
  const context = useAuthContext();
  return (
    context.formErrors["__all__"] ??
    context.formErrors["non_field_errors"] ??
    []
  );
}
